import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import type { ComponentChildren } from 'preact';
import { dataHub, type OrdoTerms, type AuthError, type ProfileInput } from '../lib/datahub';
import { AdminPanel } from './AdminPanel';
import { SettingsPanel } from './SettingsPanel';
import { TwoFactorPanel } from './TwoFactorPanel';
import './auth-gate.css';

// Native Preact port of the Oculus-Clipeus gate (was trading-suite/datahub-client.js).
// Renders EITHER the full-screen gate overlay OR the app (children) — never both — so the
// cockpit is never exposed before authentication. Five gated views mirror the legacy flow:
// sign-in -> (Create New User) -> register -> Terms of Use -> pending approval, plus a
// forced change-password view. The token lives in sessionStorage (DataHubClient owns it).

type View = 'loading' | 'signin' | 'register' | 'terms' | 'pending' | 'changePassword' | 'authed';

const REMEMBER_USER_KEY = 'ordoRememberedUsername';
const readRememberedUser = (): string => {
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem(REMEMBER_USER_KEY)) || '';
  } catch {
    return '';
  }
};
const writeRememberedUser = (u: string): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    if (u) localStorage.setItem(REMEMBER_USER_KEY, u);
    else localStorage.removeItem(REMEMBER_USER_KEY);
  } catch {
    /* no storage */
  }
};

const EyeOpen = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M2.1 12s3.6-6 9.9-6 9.9 6 9.9 6-3.6 6-9.9 6-9.9-6-9.9-6Z" fill="none" stroke="currentColor" stroke-width="2" />
    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="2" />
  </svg>
);
const EyeOff = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m3 3 18 18" fill="none" stroke="currentColor" stroke-width="2" />
    <path
      d="M10.6 5.1A10.7 10.7 0 0 1 12 5c6.3 0 9.9 7 9.9 7a19.2 19.2 0 0 1-3.1 3.7M6.6 6.8A18.2 18.2 0 0 0 2.1 12s3.6 7 9.9 7a10.8 10.8 0 0 0 4-.8"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
    />
  </svg>
);

function PwField(props: {
  id: string;
  label: string;
  value: string;
  onInput: (v: string) => void;
  autocomplete: string;
  placeholder?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <>
      <label for={props.id}>{props.label}</label>
      <div class="om-auth-pwfield">
        <input
          id={props.id}
          type={show ? 'text' : 'password'}
          autocomplete={props.autocomplete}
          placeholder={props.placeholder}
          value={props.value}
          onInput={(e) => props.onInput((e.target as HTMLInputElement).value)}
        />
        <button class="om-auth-eye" type="button" aria-label={show ? 'Hide password' : 'Show password'} onClick={() => setShow((s) => !s)}>
          {show ? <EyeOff /> : <EyeOpen />}
        </button>
      </div>
    </>
  );
}

export function AuthGate({ children }: { children: ComponentChildren }) {
  const [view, setView] = useState<View>('loading');
  const [layer, setLayer] = useState('Oculus-Clipeus');
  const [multiUser, setMultiUser] = useState(true);
  const [pinMode, setPinMode] = useState(false);
  const signinForm = useRef<HTMLFormElement>(null);
  const [role, setRole] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lockSlot, setLockSlot] = useState<HTMLElement | null>(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [needCode, setNeedCode] = useState(false);   // per-user 2FA: server asked for a code mid-login

  // sign-in
  const [username, setUsername] = useState(readRememberedUser());
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(Boolean(readRememberedUser()));

  // register
  const [regUser, setRegUser] = useState('');
  const [regPass, setRegPass] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regName, setRegName] = useState('');
  const [regBirthday, setRegBirthday] = useState('');
  const [regEmail, setRegEmail] = useState('');

  // terms
  const [terms, setTerms] = useState<OrdoTerms | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [account, setAccount] = useState<{ username: string; password: string; profile: ProfileInput } | null>(null);
  const [pendingUser, setPendingUser] = useState('');

  // change password
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNext, setCpNext] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpUser, setCpUser] = useState('');

  useEffect(() => {
    let live = true;
    dataHub
      .authStatus()
      .then((s) => {
        if (!live) return;
        setLayer((s.layer as string) || 'Oculus-Clipeus');
        // Only the edge worker (USERS_DB bound) reports multiUser:true and serves
        // register/terms. The local hub (desktop) omits the field and is owner-only, so a
        // strict === true keeps signup/username UI off there (avoids 404s on /api/auth/register).
        setMultiUser(s.multiUser === true);
        setPinMode(s.pinMode === true);
        if (s.authEnabled === false) {
          setView('authed'); // auth disabled -> no gate
          return;
        }
        if (s.authenticated && s.mustChangePassword) {
          setCpUser((s.user as string) || '');
          setView('changePassword');
          return;
        }
        if (s.authenticated) {
          setRole(s.role || undefined);
          setView('authed');
          return;
        }
        setView('signin');
      })
      .catch((e) => {
        if (!live) return;
        // Auth status unknown is LOCKED by design — no offline/manual bypass.
        setError(`DataHub unreachable. The suite stays locked until Oculus-Clipeus can verify this session. (${(e as Error).message})`);
        setView('signin');
      });
    return () => {
      live = false;
    };
  }, []);

  // Once the app (Shell) renders, dock the lock pill into the rail's reserved #omLockSlot.
  useEffect(() => {
    if (view === 'authed') setLockSlot(document.getElementById('omLockSlot'));
  }, [view]);

  const goSignin = useCallback(() => {
    setError(null);
    setBusy(false);
    setView('signin');
  }, []);

  const onSignin = async (e: Event) => {
    e.preventDefault();
    if (!password) {
      setError('Enter your passphrase.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await dataHub.login(password, multiUser ? username : undefined, code.trim() ? code : undefined);
      writeRememberedUser(remember && username ? username.trim().toLowerCase() : '');
      setCode('');
      setNeedCode(false);
      if (r.mustChangePassword) {
        setCpCurrent(password);
        setCpUser(username || '');
        setPassword('');
        setBusy(false);
        setError(null);
        setView('changePassword');
        return;
      }
      setPassword('');
      setRole(r.role);
      setBusy(false);
      setView('authed');
    } catch (ex) {
      const err = ex as AuthError;
      setBusy(false);
      if (err.pending) {
        setPendingUser(username);
        setView('pending');
        return;
      }
      if (err.twoFactorRequired) {
        setNeedCode(true);   // reveal the code field, keep the password, let them complete sign-in
        setError(err.message || 'Enter your 6-digit authenticator code.');
        return;
      }
      setError(err.message || 'Sign-in failed.');
    }
  };

  const onRegisterContinue = async (e: Event) => {
    e.preventDefault();
    const u = regUser.trim().toLowerCase();
    if (!/^[a-z0-9._-]{3,32}$/.test(u)) {
      setError('Username must be 3-32 chars: a-z 0-9 . _ -');
      return;
    }
    if (u === 'owner') {
      setError('That username is reserved.');
      return;
    }
    if (regPass.length < 10) {
      setError('Password must be at least 10 characters.');
      return;
    }
    if (regPass !== regConfirm) {
      setError('Passwords do not match.');
      return;
    }
    if (regName.trim().length < 2) {
      setError('Enter your full name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(regBirthday)) {
      setError('Enter your birthday.');
      return;
    }
    {
      // Client-side 18+ hint; the server enforces it authoritatively.
      const [by, bm, bd] = regBirthday.split('-').map(Number);
      const now = new Date();
      let age = now.getUTCFullYear() - by;
      if (now.getUTCMonth() < bm - 1 || (now.getUTCMonth() === bm - 1 && now.getUTCDate() < bd)) age -= 1;
      if (!(age >= 18 && age <= 120)) {
        setError(age < 18 ? 'You must be at least 18 to use Ordo Mentis.' : 'Enter a valid birthday.');
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      const res = await dataHub.getTerms();
      setTerms(res.terms || {});
      setAccount({ username: u, password: regPass, profile: { fullName: regName.trim(), birthday: regBirthday, email: regEmail.trim().toLowerCase() } });
      setAgreed(false);
      setBusy(false);
      setView('terms');
    } catch {
      setBusy(false);
      setError('Could not load the Terms of Use. Check your connection and try again.');
    }
  };

  const onAcceptTerms = async (e: Event) => {
    e.preventDefault();
    if (!agreed) {
      setError('You must accept the Terms of Use to create an account.');
      return;
    }
    if (!account || !terms) return;
    setBusy(true);
    setError(null);
    try {
      await dataHub.register(account.username, account.password, terms.version || '', account.profile);
      setPendingUser(account.username);
      setRegPass('');
      setRegConfirm('');
      setRegName('');
      setRegBirthday('');
      setRegEmail('');
      setAccount(null);
      setBusy(false);
      setView('pending');
    } catch (ex) {
      const err = ex as AuthError;
      setBusy(false);
      if (err.termsChanged) {
        try {
          const res = await dataHub.getTerms();
          setTerms(res.terms || {});
          setAgreed(false);
          setError('The Terms changed — please review and accept the latest version.');
        } catch {
          setError('Terms changed; could not reload. Try again.');
        }
        return;
      }
      setError(err.message || 'Could not create the account.');
    }
  };

  const onChangePassword = async (e: Event) => {
    e.preventDefault();
    if (cpNext.length < 10) {
      setError('Use at least 10 characters for the new password.');
      return;
    }
    if (cpNext !== cpConfirm) {
      setError('The new passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await dataHub.changePassword(cpCurrent, cpNext);
      setCpCurrent('');
      setCpNext('');
      setCpConfirm('');
      setRole(r.role);
      setBusy(false);
      setView('authed');
    } catch (ex) {
      setBusy(false);
      setError((ex as Error).message || 'Password change failed.');
    }
  };

  const onLock = () => {
    dataHub.logout();
    setPassword('');
    setRole(undefined);
    setError(null);
    setView('signin');
  };

  if (view === 'authed') {
    const pill = (
      <div class={`om-lock-pill${lockSlot ? '' : ' floating'}`}>
        <span>
          <strong>Oculus-Clipeus</strong> unlocked{role ? ` - ${role}` : ''}
        </span>
        <span class="om-lock-actions">
          {role === 'owner' && (
            <button type="button" onClick={() => setSettingsOpen(true)}>
              Settings
            </button>
          )}
          {role === 'owner' && multiUser && (
            <button type="button" onClick={() => setAdminOpen(true)}>
              Accounts
            </button>
          )}
          {role !== 'owner' && (
            <button type="button" onClick={() => setSecurityOpen(true)}>
              Security
            </button>
          )}
          <button type="button" onClick={onLock}>
            Lock
          </button>
        </span>
      </div>
    );
    return (
      <>
        {children}
        {lockSlot ? createPortal(pill, lockSlot) : pill}
        {adminOpen && createPortal(<AdminPanel onClose={() => setAdminOpen(false)} />, document.body)}
        {settingsOpen && createPortal(<SettingsPanel onClose={() => setSettingsOpen(false)} />, document.body)}
        {securityOpen && createPortal(<TwoFactorPanel onClose={() => setSecurityOpen(false)} />, document.body)}
      </>
    );
  }

  if (view === 'loading') {
    return (
      <div class="om-auth-gate" role="status" aria-live="polite">
        <div class="om-auth-card">
          <p class="om-auth-splash">Checking Oculus-Clipeus...</p>
        </div>
      </div>
    );
  }

  return (
    <div class="om-auth-gate" role="dialog" aria-modal="true">
      {view === 'signin' && (
        <form class="om-auth-card" autocomplete="on" onSubmit={onSignin} ref={signinForm}>
          <p class="om-auth-kicker">Oculus-Clipeus</p>
          <h2>Unlock Ordo Mentis</h2>
          <p>{pinMode ? 'Enter your 4-digit PIN to unlock.' : 'Use the owner passphrase, or enter a provisioned username plus password.'}</p>
          {multiUser && !pinMode && (
            <>
              <label for="omAuthUser">
                Username <span class="om-auth-hint">(optional for owner)</span>
              </label>
              <input
                id="omAuthUser"
                type="text"
                autocomplete="username"
                autocapitalize="none"
                value={username}
                onInput={(e) => setUsername((e.target as HTMLInputElement).value)}
              />
            </>
          )}
          {pinMode ? (
            <>
              <label for="omAuthPin">PIN</label>
              <input
                id="omAuthPin"
                class="om-auth-pin"
                type="password"
                inputMode="numeric"
                autocomplete="off"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autofocus
                maxLength={4}
                aria-label="4-digit PIN"
                value={password}
                onInput={(e) => {
                  const v = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 4);
                  setPassword(v);
                  // Auto-submit once 4 digits are in (the expected PIN-pad feel).
                  if (v.length === 4 && !busy) setTimeout(() => signinForm.current?.requestSubmit(), 0);
                }}
              />
            </>
          ) : (
            <PwField id="omAuthPass" label="Passphrase / password" value={password} onInput={setPassword} autocomplete="current-password" />
          )}
          {needCode && (
            <>
              <label for="omAuthCode">6-digit code</label>
              <input
                id="omAuthCode"
                type="text"
                inputMode="numeric"
                autocomplete="one-time-code"
                maxLength={6}
                value={code}
                onInput={(e) => setCode((e.target as HTMLInputElement).value)}
              />
            </>
          )}
          {multiUser && (
            <label class="om-auth-row">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember((e.target as HTMLInputElement).checked)} /> Remember username on this browser
            </label>
          )}
          {error && <div class="om-auth-error">{error}</div>}
          <div class="om-auth-actions">
            <button class="om-auth-primary" type="submit" disabled={busy}>
              {busy ? 'Unlocking...' : 'Unlock'}
            </button>
            {multiUser && (
              <button
                class="om-auth-secondary"
                type="button"
                onClick={() => {
                  setError(null);
                  setRegUser('');
                  setRegPass('');
                  setRegConfirm('');
                  setView('register');
                }}
              >
                Create New User
              </button>
            )}
          </div>
          <p class="om-auth-hint">Layer: {layer}</p>
        </form>
      )}

      {view === 'register' && (
        <form class="om-auth-card" autocomplete="on" onSubmit={onRegisterContinue}>
          <p class="om-auth-kicker">Oculus-Clipeus</p>
          <h2>Create New User</h2>
          <p>
            Choose a username and password. New accounts are reviewed by the owner before online access is enabled; until then, offline/manual mode
            is available.
          </p>
          <label for="omRegUser">Username</label>
          <input
            id="omRegUser"
            type="text"
            autocomplete="username"
            autocapitalize="none"
            placeholder="3-32 chars: a-z 0-9 . _ -"
            value={regUser}
            onInput={(e) => setRegUser((e.target as HTMLInputElement).value)}
          />
          <PwField id="omRegPass" label="Password" value={regPass} onInput={setRegPass} autocomplete="new-password" placeholder="At least 10 characters" />
          <label for="omRegConfirm">Confirm password</label>
          <input
            id="omRegConfirm"
            type="password"
            autocomplete="new-password"
            value={regConfirm}
            onInput={(e) => setRegConfirm((e.target as HTMLInputElement).value)}
          />
          <label for="omRegName">Full name</label>
          <input
            id="omRegName"
            type="text"
            autocomplete="name"
            placeholder="First Last"
            value={regName}
            onInput={(e) => setRegName((e.target as HTMLInputElement).value)}
          />
          <label for="omRegEmail">Email</label>
          <input
            id="omRegEmail"
            type="email"
            autocomplete="email"
            autocapitalize="none"
            placeholder="you@example.com"
            value={regEmail}
            onInput={(e) => setRegEmail((e.target as HTMLInputElement).value)}
          />
          <label for="omRegBirthday">Date of birth</label>
          <input
            id="omRegBirthday"
            type="date"
            autocomplete="bday"
            value={regBirthday}
            onInput={(e) => setRegBirthday((e.target as HTMLInputElement).value)}
          />
          {error && <div class="om-auth-error">{error}</div>}
          <div class="om-auth-actions">
            <button class="om-auth-primary" type="submit" disabled={busy}>
              {busy ? 'Loading Terms...' : 'Continue to Terms'}
            </button>
            <button class="om-auth-secondary" type="button" onClick={goSignin}>
              Back to sign in
            </button>
          </div>
        </form>
      )}

      {view === 'terms' && terms && (
        <form class="om-auth-card" autocomplete="off" onSubmit={onAcceptTerms}>
          <p class="om-auth-kicker">Terms of Use{terms.draft ? ' (Draft)' : ''}</p>
          <h2>{terms.title || 'Terms of Use'}</h2>
          <p>
            Version {terms.version || '-'}
            {terms.effectiveDate ? ` · Effective ${terms.effectiveDate}` : ''}
          </p>
          <div class="om-auth-terms-body">
            {(terms.body || '').split('\n').map((line, i) => (
              <span key={i}>
                {line}
                <br />
              </span>
            ))}
            {terms.acknowledgments && terms.acknowledgments.length > 0 && (
              <ul>
                {terms.acknowledgments.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            )}
          </div>
          <label class="om-auth-row">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => {
                setAgreed((e.target as HTMLInputElement).checked);
                setError(null);
              }}
            />{' '}
            I have read and agree to the Terms of Use.
          </label>
          {error && <div class="om-auth-error">{error}</div>}
          <div class="om-auth-actions">
            <button class="om-auth-primary" type="submit" disabled={busy}>
              {busy ? 'Creating...' : 'Create account'}
            </button>
            <button class="om-auth-secondary" type="button" onClick={goSignin}>
              Decline
            </button>
          </div>
        </form>
      )}

      {view === 'pending' && (
        <div class="om-auth-card">
          <p class="om-auth-kicker">Oculus-Clipeus</p>
          <h2>Account pending approval</h2>
          <p>
            {pendingUser ? (
              <>
                Account <strong>{pendingUser}</strong> was created.{' '}
              </>
            ) : null}
            The owner must approve it before online access (DataHub, Caesar, paper sync) is enabled. You can sign in once it is approved; until then,
            offline/manual mode only.
          </p>
          <div class="om-auth-actions">
            <button class="om-auth-primary" type="button" onClick={goSignin}>
              Back to sign in
            </button>
          </div>
        </div>
      )}

      {view === 'changePassword' && (
        <form class="om-auth-card" autocomplete="on" onSubmit={onChangePassword}>
          <p class="om-auth-kicker">Oculus-Clipeus</p>
          <h2>Change Password</h2>
          <p>The temporary password can only reach this screen. Set a private password before calculators unlock.</p>
          <PwField id="omCpCurrent" label="Current password" value={cpCurrent} onInput={setCpCurrent} autocomplete="current-password" />
          <label for="omCpNew">New password</label>
          <input
            id="omCpNew"
            type="password"
            autocomplete="new-password"
            value={cpNext}
            onInput={(e) => setCpNext((e.target as HTMLInputElement).value)}
          />
          <label for="omCpConfirm">Confirm new password</label>
          <input
            id="omCpConfirm"
            type="password"
            autocomplete="new-password"
            value={cpConfirm}
            onInput={(e) => setCpConfirm((e.target as HTMLInputElement).value)}
          />
          {error && <div class="om-auth-error">{error}</div>}
          <div class="om-auth-actions">
            <button class="om-auth-primary" type="submit" disabled={busy}>
              {busy ? 'Saving...' : 'Save password'}
            </button>
          </div>
          {cpUser && <p class="om-auth-hint">Account: {cpUser}</p>}
        </form>
      )}
    </div>
  );
}
